import * as PIXI from 'pixi.js';
import type { AssetLoadProgress } from '@/managers/AssetManager';

const fontFamily = `"VT323", "Courier New", monospace, serif`;

const COLORS = {
  BACKGROUND: 0x000000,
  BACKGROUND_ALPHA: 0.96,
  TEXT: 0xd0c097,
  HIGHLIGHT: 0xc59a23,
  PROGRESS_BG: 0x2f2a1f,
  PROGRESS_BORDER: 0x544c36,
  PROGRESS_FILL: 0xc59a23,
  PROGRESS_TEXT: 0xb8aa85,
  TIP_TEXT: 0xbcb19a,
};

const DEVICE_TUTORIAL_IMAGES = {
  pc: ['assets/ui/Tutorial-PC.png'],
  mobile: ['assets/ui/Tutorial-Mobile.png'],
};

const SPLASH_TIPS = [
  'Perhatikan objek yang terlihat berbeda, biasanya bisa diinteraksi.',
  'Dekati NPC dan tekan tombol interaksi untuk membuka dialog baru.',
  'Gunakan inventory untuk cek item penting sebelum lanjut misi.',
  'Area tertentu menyimpan petunjuk tambahan kalau kamu eksplor lebih jauh.',
  'Kalau buntu, coba keliling ulang area dan cek sudut yang terlewat.',
];

export class SplashScreen {
  private container: PIXI.Container;
  private background: PIXI.Graphics;
  private isVisible: boolean = true;
  private isLoading: boolean = false;
  private loadingContainer: PIXI.Container;
  private loadingText: PIXI.Text;
  private progressBar: PIXI.Graphics;
  private progressBarBg: PIXI.Graphics;
  private outerBorder: PIXI.Graphics;
  private tutorialImage: PIXI.Sprite;
  private tutorialTextContainer: PIXI.Container;
  private tipsLabel: PIXI.Text;
  private tipsText: PIXI.Text;
  private isShowingProgress: boolean = false;
  private currentScreenWidth: number;
  private currentScreenHeight: number;
  private currentTipIndex: number = -1;
  private nextTipSwitchAt: number = 0;
  private readonly barWidth = 360;
  private readonly tipSwitchIntervalMs = 3500;

  constructor(screenWidth: number, screenHeight: number) {
    this.container = new PIXI.Container();
    this.container.sortableChildren = true;
    this.currentScreenWidth = screenWidth;
    this.currentScreenHeight = screenHeight;

    this.background = new PIXI.Graphics();
    this.background.beginFill(COLORS.BACKGROUND, COLORS.BACKGROUND_ALPHA);
    this.background.drawRect(0, 0, screenWidth, screenHeight);
    this.background.endFill();
    this.container.addChild(this.background);

    this.loadingContainer = new PIXI.Container();

    this.tutorialImage = new PIXI.Sprite();
    this.tutorialImage.anchor.set(0.5);
    this.tutorialImage.position.set(0, -110);
    this.loadingContainer.addChild(this.tutorialImage);
    this.loadTutorialImage();

    // Build tutorial text with "Panduan Dibawah" highlighted in gold
    this.tutorialTextContainer = new PIXI.Container();
    const textStyle = { fontFamily, fontSize: 16, fill: COLORS.TEXT, align: 'left' as const, letterSpacing: 1, padding: 4 };
    const highlightStyle = { ...textStyle, fill: COLORS.HIGHLIGHT };

    const part1 = new PIXI.Text('Gunakan ', textStyle);
    const part2 = new PIXI.Text('Panduan Dibawah', highlightStyle);
    const part3 = new PIXI.Text(' Untuk Berjelajah, Berinteraksi Dan Menyelesaikan Misi.', textStyle);
    part1.anchor.set(0, 0.5);
    part2.anchor.set(0, 0.5);
    part3.anchor.set(0, 0.5);

    // Layout inline
    part1.position.set(0, 0);
    part2.position.set(part1.width, 0);
    part3.position.set(part1.width + part2.width, 0);

    this.tutorialTextContainer.addChild(part1, part2, part3);
    // Center the container by offsetting by half its total width
    const totalTextWidth = part1.width + part2.width + part3.width;
    this.tutorialTextContainer.pivot.set(totalTextWidth / 2, 0);
    this.loadingContainer.addChild(this.tutorialTextContainer);

    this.tutorialImage.visible = true;
    this.tutorialTextContainer.visible = true;

    this.loadingText = new PIXI.Text('Loading...', {
      fontFamily,
      fontSize: 18,
      fill: COLORS.TEXT,
      align: 'center',
      letterSpacing: 1,
      padding: 4,
    });
    this.loadingText.anchor.set(0.5);
    this.loadingContainer.addChild(this.loadingText);

    this.outerBorder = new PIXI.Graphics();
    this.outerBorder.beginFill(COLORS.PROGRESS_BORDER);
    this.outerBorder.drawRoundedRect(-this.barWidth / 2, -11, this.barWidth, 22, 6);
    this.outerBorder.endFill();
    this.loadingContainer.addChild(this.outerBorder);

    this.progressBarBg = new PIXI.Graphics();
    this.progressBarBg.beginFill(COLORS.PROGRESS_BG);
    this.progressBarBg.drawRoundedRect(-this.barWidth / 2 + 3, -8, this.barWidth - 6, 16, 5);
    this.progressBarBg.endFill();
    this.loadingContainer.addChild(this.progressBarBg);

    this.progressBar = new PIXI.Graphics();
    this.loadingContainer.addChild(this.progressBar);

    this.tipsLabel = new PIXI.Text('Tips :', {
      fontFamily,
      fontSize: 16,
      fill: COLORS.HIGHLIGHT,
      align: 'center',
      letterSpacing: 1,
      padding: 4,
    });
    this.tipsLabel.anchor.set(0.5, 0);
    this.loadingContainer.addChild(this.tipsLabel);

    this.tipsText = new PIXI.Text('', {
      fontFamily,
      fontSize: 16,
      fill: COLORS.TIP_TEXT,
      align: 'center',
      letterSpacing: 1,
      lineHeight: 22,
      padding: 4,
      wordWrap: true,
      wordWrapWidth: this.barWidth * 1.8,
    });
    this.tipsText.anchor.set(0.5, 0);
    this.loadingContainer.addChild(this.tipsText);

    this.refreshTips(true);

    this.loadingContainer.position.set(screenWidth / 2, screenHeight / 2);
    this.container.addChild(this.loadingContainer);
    this.container.zIndex = 1000;
    this.layoutElements(screenWidth, screenHeight);
  }

  public getContainer(): PIXI.Container { return this.container; }
  public show(): void { this.isVisible = true; this.container.visible = true; }
  public hide(): void { this.isVisible = false; this.container.visible = false; }
  public isShowing(): boolean { return this.isVisible; }
  public onStart(callback: () => void): void { setTimeout(() => callback(), 100); }

  public updateScreenSize(width: number, height: number): void {
    this.currentScreenWidth = width;
    this.currentScreenHeight = height;
    this.background.clear();
    this.background.beginFill(COLORS.BACKGROUND, COLORS.BACKGROUND_ALPHA);
    this.background.drawRect(0, 0, width, height);
    this.background.endFill();
    this.loadingContainer.position.set(width / 2, height / 2);
    this.layoutElements(width, height);
  }

  public showLoading(): void {
    this.isLoading = true;
    this.isShowingProgress = true;
    this.loadingContainer.visible = true;
    this.refreshTips(true);
  }

  public updateProgress(progress: AssetLoadProgress): void {
    if (!this.isShowingProgress) return;
    this.progressBar.clear();
    const width = (progress.percentage / 100) * 348;
    this.progressBar.beginFill(COLORS.PROGRESS_FILL);
    this.progressBar.drawRoundedRect(0, 0, width, 10, 4);
    this.progressBar.endFill();

    if (progress.stage === 'images') this.loadingText.text = 'Loading Images...';
    else if (progress.stage === 'audio') this.loadingText.text = 'Loading Audio...';
    else if (progress.stage === 'complete') this.loadingText.text = 'Loading Complete!';
    else this.loadingText.text = 'Loading...';
  }

  public update(): void {
    if (!this.isShowingProgress) return;
    if (Date.now() >= this.nextTipSwitchAt) {
      this.refreshTips();
    }
  }

  public hideLoading(): void {
    this.isLoading = false;
    this.isShowingProgress = false;
    this.loadingContainer.visible = false;
  }

  private isMobileDevice(): boolean {
    const ua = navigator.userAgent || '';
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
  }

  private async loadTutorialImage(): Promise<void> {
    const key = this.isMobileDevice() ? 'mobile' : 'pc';
    const items = DEVICE_TUTORIAL_IMAGES[key];
    const randomPath = items[Math.floor(Math.random() * items.length)];
    
    try {
      const tx = await PIXI.Assets.load(`/${randomPath}`);
      if (this.tutorialImage && tx) {
        this.tutorialImage.texture = tx;
        // Re-layout everything now that the texture is loaded
        this.layoutElements(this.currentScreenWidth, this.currentScreenHeight);
      }
    } catch (e) {
      console.warn('Failed to load tutorial image', e);
    }
  }

  /**
   * Positions all splash elements. All positions are FIXED and do not change
   * when the tutorial image loads. The image simply fills the space between
   * the text and the loading bar.
   */
  private layoutElements(screenWidth: number, screenHeight: number): void {
    const halfHeight = screenHeight / 2;

    // --- Proportional positions (relative to container center at y=0) ---

    // Text at ~30% above center
    const textY = -halfHeight * 0.6;
    this.tutorialTextContainer.position.set(0, textY);

    // Scale text down if wider than screen
    this.tutorialTextContainer.scale.set(1);
    const textNaturalWidth = this.tutorialTextContainer.width;
    const maxTextWidth = screenWidth * 0.9;
    if (textNaturalWidth > maxTextWidth) {
      this.tutorialTextContainer.scale.set(maxTextWidth / textNaturalWidth);
    }

    // Loading text + progress bar at ~40% below center
    const progressBarY = halfHeight * 0.45;
    const loadingTextY = progressBarY - 36;
    const tipsLabelY = progressBarY + 24;
    const tipsTextY = tipsLabelY + 22;

    this.loadingText.position.set(0, loadingTextY);
    this.outerBorder.y = progressBarY;
    this.progressBarBg.y = progressBarY;
    this.progressBar.position.set(-this.barWidth / 2 + 6, progressBarY - 8);
    this.tipsLabel.position.set(0, tipsLabelY);
    this.tipsText.style.wordWrapWidth = Math.min(this.barWidth * 1.8, screenWidth * 0.86);
    this.tipsText.position.set(0, tipsTextY);

    const contentTop = textY + 30;
    const contentBottom = loadingTextY - 10;

    // Scale and position tutorial image in the space between text and loading bar
    this.scaleTutorialImage(screenWidth, contentTop + 10, contentBottom);
  }

  /**
   * Scales and positions the tutorial image in the space between contentTop and contentBottom.
   * Does NOT move any other element.
   */
  private scaleTutorialImage(screenWidth: number, topBound: number, bottomBound: number): void {
    const tx = this.tutorialImage.texture;
    if (!tx || tx.width === 0 || tx.height === 0) return;

    const availableHeight = bottomBound - topBound;
    const availableWidth = Math.min(820, screenWidth * 0.85);

    const scale = Math.min(availableWidth / tx.width, availableHeight / tx.height, 1);
    this.tutorialImage.scale.set(scale);

    // Center image vertically in the available space
    const centerY = (topBound + bottomBound) / 2;
    this.tutorialImage.position.set(0, centerY);
  }

  private refreshTips(force: boolean = false): void {
    if (!force && SPLASH_TIPS.length === 0) return;
    const nextTip = this.pickNextTip();
    this.tipsText.text = nextTip;
    this.nextTipSwitchAt = Date.now() + this.tipSwitchIntervalMs;
    this.layoutElements(this.currentScreenWidth, this.currentScreenHeight);
  }

  private pickNextTip(): string {
    if (SPLASH_TIPS.length === 0) return '';
    if (SPLASH_TIPS.length === 1) {
      this.currentTipIndex = 0;
      return SPLASH_TIPS[0];
    }

    let nextIndex = this.currentTipIndex;
    while (nextIndex === this.currentTipIndex) {
      nextIndex = Math.floor(Math.random() * SPLASH_TIPS.length);
    }
    this.currentTipIndex = nextIndex;
    return SPLASH_TIPS[nextIndex];
  }

  public updateLoadingText(text: string): void { this.loadingText.text = text; }
}
